<div class="page-content">
  <!-- Header Section -->
  <div class="page-title-container">
    <div class="breadcrumb-spacer"></div>
    <h1 class="page-title">System Logs</h1>
  </div>

  <div class="logs-container">
    <!-- Filters Section -->
    <mat-card class="filters-card">
      <mat-card-content>
        <form [formGroup]="filterForm" class="filters-form">
          <div class="filter-row">
            <!-- Search Field -->
            <mat-form-field appearance="outline" class="search-field">
              <mat-label>Search system logs</mat-label>
              <input
                matInput
                [(ngModel)]="searchQuery"
                [ngModelOptions]="{ standalone: true }"
                placeholder="Search by message..."
                autocomplete="off"
                (input)="onSearchInput()"
              />
              <mat-icon
                matSuffix
                [style.cursor]="searchQuery ? 'pointer' : 'default'"
                (click)="clearSearch()"
                >{{ searchQuery ? "close" : "search" }}</mat-icon
              >
            </mat-form-field>

            <!-- Log Level Filter -->
            <mat-form-field appearance="outline" class="level-field">
              <mat-label>Log Level</mat-label>
              <mat-select formControlName="levels" multiple>
                @for (level of logLevels; track level) {
                  <mat-option [value]="level">
                    {{ level }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>

            <!-- Start Date/Time Filter -->
            <mat-form-field appearance="outline" class="datetime-field">
              <mat-label>Start Date/Time</mat-label>
              <input
                matInput
                type="datetime-local"
                formControlName="startDate"
                (change)="onDateChange()"
              />
            </mat-form-field>

            <!-- End Date/Time Filter -->
            <mat-form-field appearance="outline" class="datetime-field">
              <mat-label>End Date/Time</mat-label>
              <input
                matInput
                type="datetime-local"
                formControlName="endDate"
                (change)="onDateChange()"
              />
            </mat-form-field>

            <!-- Reset Button -->
            <button
              mat-stroked-button
              type="button"
              (click)="resetFilters()"
              class="reset-filters-btn"
              [disabled]="!hasFiltersChanged()"
            >
              <mat-icon>replay</mat-icon>
              Reset
            </button>
          </div>
        </form>
      </mat-card-content>
    </mat-card>

    <!-- Loading State -->
    @if (isLoading) {
      <div class="loading-container">
        <mat-spinner diameter="40"></mat-spinner>
        <span>Loading logs...</span>
      </div>
    }

    <!-- Error State -->
    @if (error && !isLoading) {
      <app-error-state [message]="error" (retry)="loadLogs()"></app-error-state>
    }

    <!-- Table Section -->
    @if (!isLoading && !error) {
      <mat-card class="table-card no-padding">
        <mat-card-content>
          <div class="table-container">
            <table
              mat-table
              [dataSource]="dataSource"
              matSort
              [matSortActive]="sortBy"
              [matSortDirection]="sortDirection"
              matSortDisableClear
              (matSortChange)="onSortChange($event)"
              class="logs-table"
            >
              <!-- Timestamp Column -->
              <ng-container matColumnDef="timestamp">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Timestamp
                </th>
                <td mat-cell *matCellDef="let log">
                  <span class="timestamp">{{
                    formatTimestamp(log.timeStamp)
                  }}</span>
                </td>
              </ng-container>

              <!-- Level Column -->
              <ng-container matColumnDef="level">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Level</th>
                <td mat-cell *matCellDef="let log">
                  <span
                    class="level-badge"
                    [ngClass]="getLevelClass(log.level)"
                  >
                    {{ log.level }}
                  </span>
                </td>
              </ng-container>

              <!-- User Column -->
              <ng-container matColumnDef="user">
                <th mat-header-cell *matHeaderCellDef>User</th>
                <td mat-cell *matCellDef="let log">
                  <span class="user">{{
                    log.employee?.displayName || log.employeeId || "-"
                  }}</span>
                </td>
              </ng-container>

              <!-- Message Column -->
              <ng-container matColumnDef="message">
                <th mat-header-cell *matHeaderCellDef>Message</th>
                <td mat-cell *matCellDef="let log">
                  <span class="message">
                    {{ truncateMessage(log.message) }}
                  </span>
                </td>
              </ng-container>

              <!-- Request Path Column -->
              <ng-container matColumnDef="requestPath">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Request Path
                </th>
                <td mat-cell *matCellDef="let log">
                  @if (log.requestPath) {
                    <span class="request-path">{{ log.requestPath }}</span>
                  } @else {
                    <span class="no-path">-</span>
                  }
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: displayedColumns"
                class="clickable-row"
                (click)="openLogDetail(row)"
              ></tr>

              <!-- Empty State -->
              <tr class="mat-row" *matNoDataRow>
                <td
                  class="mat-cell no-data-cell"
                  [attr.colspan]="displayedColumns.length"
                >
                  <div class="empty-state">
                    <mat-icon>search_off</mat-icon>
                    <span>No system logs found matching your filters</span>
                  </div>
                </td>
              </tr>
            </table>
          </div>

          <mat-paginator
            [length]="totalCount"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            (page)="onPageChange($event)"
            showFirstLastButtons
          >
          </mat-paginator>
        </mat-card-content>
      </mat-card>
    }
  </div>
</div>
